/**
 * Restores document/body scrolling on web for public marketing/legal pages.
 * Authenticated fullscreen layout injects styles that can suppress scroll; this
 * hook mirrors cleanup applied on the landing page when those routes mount.
 *
 * Do **not** enable this inside the authenticated tab shell (`/app/legal/*`):
 * forcing root/body to `height: auto` breaks the flex chain so tab scenes can
 * collapse to zero height (blank black viewport on web).
 */

import { useEffect } from 'react'
import { Platform } from 'react-native'

export function usePublicMarketingWebScrollLayout(enabled = true): void {
  useEffect(() => {
    if (!enabled) return
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const fullscreenStyle = document.getElementById(
      'authenticated-fullscreen-style'
    )
    if (fullscreenStyle && document.head.contains(fullscreenStyle)) {
      document.head.removeChild(fullscreenStyle)
    }

    document.body.style.removeProperty('overflow')
    document.documentElement.style.removeProperty('overflow')
    document.body.style.setProperty('overflow-x', 'hidden', 'important')
    document.body.style.setProperty('overflow-y', 'visible', 'important')
    document.documentElement.style.setProperty(
      'overflow-x',
      'hidden',
      'important'
    )
    document.documentElement.style.setProperty(
      'overflow-y',
      'auto',
      'important'
    )
    document.body.style.setProperty('position', 'relative', 'important')
    document.documentElement.style.setProperty(
      'position',
      'relative',
      'important'
    )
    document.body.style.setProperty('height', 'auto', 'important')
    document.documentElement.style.setProperty('height', 'auto', 'important')

    const root =
      document.getElementById('root') ||
      document.querySelector('[data-reactroot]') ||
      document.querySelector('body > div:first-child')

    if (root) {
      ;(root as HTMLElement).style.setProperty(
        'overflow',
        'visible',
        'important'
      )
      ;(root as HTMLElement).style.setProperty(
        'position',
        'relative',
        'important'
      )
      ;(root as HTMLElement).style.setProperty('height', 'auto', 'important')
    }

    return () => {
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow-x')
      document.body.style.removeProperty('overflow-y')
      document.body.style.removeProperty('position')
      document.body.style.removeProperty('height')
      document.documentElement.style.removeProperty('overflow')
      document.documentElement.style.removeProperty('overflow-x')
      document.documentElement.style.removeProperty('overflow-y')
      document.documentElement.style.removeProperty('position')
      document.documentElement.style.removeProperty('height')
      if (root) {
        ;(root as HTMLElement).style.removeProperty('overflow')
        ;(root as HTMLElement).style.removeProperty('position')
        ;(root as HTMLElement).style.removeProperty('height')
      }
    }
  }, [enabled])
}
